// 經 VMS façade（plan/vms-system-v0.md §4）取雲端錄影索引與事件：列表單次最多 24 小時、穩定 cursor 翻頁。
import { cloudClipSpans, kinioFetch, planeBase, vms, type SourceSpan, type VmsFacadeEvent } from '@kinio/web-sdk';

import { clipRangeSuspect, guardClips, type ClipRange } from '../features/events/clipGuard';

const DAY = 24 * 3600;

/** [fromSec, toSec) 切成 ≤ 24 小時的窗（DST 日是 25 小時）。 */
export function windows(fromSec: number, toSec: number, max = DAY): [number, number][] {
  const out: [number, number][] = [];
  for (let s = fromSec; s < toSec; s += max) out.push([s, Math.min(toSec, s + max)]);
  return out;
}

const iso = (sec: number) => new Date(sec * 1000).toISOString();

/** 雲端片段 → 時間軸的 span（SDK unified.ts 的格式）。 */
export async function cloudSpans(api: string, homeId: string, device: string, fromSec: number, toSec: number, maxPages = 40): Promise<SourceSpan[]> {
  return (await cloudSpansGuarded(api, homeId, device, fromSec, toSec, maxPages)).spans;
}

/**
 * 雲端片段 + 片段長度防呆（第六輪 R6-P0-1、B6-01；clipGuard.ts）：長度不合理或結束在未來的片段截短，
 * 時間軸不畫未來、不蓋住之後的片段；可疑的時段另外回（[start, end) 秒）。
 */
export async function cloudSpansGuarded(
  api: string,
  homeId: string,
  device: string,
  fromSec: number,
  toSec: number,
  maxPages = 40,
  now = Date.now() / 1000,
  lens = 0,
): Promise<{ spans: SourceSpan[]; suspect: ClipRange[] }> {
  const clips: { start: number; end: number; has_event?: boolean }[] = [];
  for (const [f, t] of windows(fromSec, toSec)) {
    let cursor: string | undefined;
    for (let i = 0; i < maxPages; i++) {
      const p = await vms.recordings(
        api,
        homeId,
        device,
        { from: iso(f), to: iso(t), source: 'cloud', limit: 200, ...(cursor ? { cursor } : {}), ...(lens ? { lens } : {}) },
      );
      for (const c of p?.data ?? []) {
        if (c.source !== 'cloud') continue;
        clips.push({ start: Date.parse(c.start_at) / 1000, end: Date.parse(c.end_at) / 1000, has_event: c.has_event });
      }
      if (!p?.next_cursor) break;
      cursor = p.next_cursor;
    }
  }
  const g = guardClips(clips, now);
  return { spans: cloudClipSpans(g.clips.map((c) => ({ clip_start_ts_us: c.start * 1e6, clip_end_ts_us: c.end * 1e6, has_event: c.has_event }))), suspect: g.suspect };
}

/**
 * 異地備份副本（façade source=backup，BAK-04 / D-12）：verified 副本的 [start, end)（秒）。
 * 沒有政策也沒有副本 → null（時間軸不列這個來源）。
 */
export async function backupSpans(api: string, homeId: string, device: string, fromSec: number, toSec: number, maxPages = 40): Promise<{ start: number; end: number }[] | null> {
  const spans: { start: number; end: number }[] = [];
  let covered = false;
  for (const [f, t] of windows(fromSec, toSec)) {
    let cursor: string | undefined;
    for (let i = 0; i < maxPages; i++) {
      const p = await vms.recordings(api, homeId, device, { from: iso(f), to: iso(t), source: 'backup', limit: 200, ...(cursor ? { cursor } : {}) });
      if (((p as { backup_coverage?: unknown[] } | undefined)?.backup_coverage ?? []).length > 0) covered = true;
      for (const c of p?.data ?? []) {
        if (c.source === 'backup') spans.push({ start: Date.parse(c.start_at) / 1000, end: Date.parse(c.end_at) / 1000 });
      }
      if (!p?.next_cursor) break;
      cursor = p.next_cursor;
    }
  }
  return spans.length === 0 && !covered ? null : spans;
}

/** 某台（或某一路）在 [fromSec, toSec) 的事件（新到舊，最多 maxPages 頁）。 */
// 多鏡頭：lens 沒帶 = 只有主鏡頭的事件（平台預設）、數字 = 那一顆、'all' = 全部（SPEC #multi-lens-recording）
export async function deviceEvents(
  api: string,
  homeId: string,
  device: string,
  fromSec: number,
  toSec: number,
  maxPages = 5,
  lens?: number | 'all',
): Promise<VmsFacadeEvent[] & { capped?: boolean }> {
  const out: VmsFacadeEvent[] & { capped?: boolean } = [];
  let cursor: string | undefined;
  for (let i = 0; i < maxPages; i++) {
    const p = await vms.events(api, homeId, { from: iso(fromSec), to: iso(toSec), device, limit: 200, ...(cursor ? { cursor } : {}), ...(lens ? { lens } : {}) });
    out.push(...(p?.data ?? []));
    if (!p?.next_cursor) break;
    cursor = p.next_cursor;
    // 翻到上限還有下一頁：數字是上限、不是全部（回放時間軸寫「1000+」，第七輪 R7-P2-6）
    if (i === maxPages - 1) out.capped = true;
  }
  return out;
}

/** 這個月有雲端錄影的日子（façade recording-days，PLAY-01 日曆；YYYY-MM-DD，tz 的日曆）。 */
export async function cloudRecordingDays(api: string, homeId: string, device: string, month: string, tz: string, lens = 0): Promise<string[]> {
  const r = await kinioFetch<{ days?: string[] }>(
    `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/vms/devices/${encodeURIComponent(device)}/recording-days?month=${month}&tz=${encodeURIComponent(tz)}${lens ? `&lens=${lens}` : ''}`,
  );
  return r?.days ?? [];
}

/**
 * 這一刻（秒）的雲端片段可疑嗎（同一時刻的縮圖，第六輪 B6-01）：問 [at − 60, at + 60] 的片段，蓋到這一刻的片段長度不合理或結束在未來 = true。
 * 讀不到 = false（不擋縮圖）。
 */
export async function cloudSuspectAt(api: string, homeId: string, device: string, atSec: number, now = Date.now() / 1000): Promise<boolean> {
  try {
    const p = await vms.recordings(api, homeId, device, { from: iso(atSec - 60), to: iso(atSec + 60), source: 'cloud', limit: 50 });
    return (p?.data ?? []).some((c) => {
      if (c.source !== 'cloud') return false;
      const r = { start: Date.parse(c.start_at) / 1000, end: Date.parse(c.end_at) / 1000 };
      return r.start <= atSec && r.end > atSec && clipRangeSuspect(r, now);
    });
  } catch {
    return false;
  }
}

/**
 * 這一刻（秒）有沒有雲端片段（同一時刻的縮圖與點擊用同一個判斷，第七輪 B7-05）：
 * 'ok' = 有片段蓋到這一刻；'suspect' = 蓋到這一刻的片段長度不合理；'none' = 沒有片段蓋到；'unknown' = 讀不到索引。
 */
export async function cloudClipAt(api: string, homeId: string, device: string, atSec: number, now = Date.now() / 1000, lens = 0): Promise<'ok' | 'suspect' | 'none' | 'unknown'> {
  try {
    const p = await vms.recordings(api, homeId, device, { from: iso(atSec - 60), to: iso(atSec + 60), source: 'cloud', limit: 50, ...(lens ? { lens } : {}) });
    const hits = (p?.data ?? [])
      .filter((c) => c.source === 'cloud')
      .map((c) => ({ start: Date.parse(c.start_at) / 1000, end: Date.parse(c.end_at) / 1000 }))
      .filter((r) => r.start <= atSec && r.end > atSec);
    if (!hits.length) return 'none';
    return hits.some((r) => !clipRangeSuspect(r, now)) ? 'ok' : 'suspect';
  } catch {
    return 'unknown';
  }
}

/**
 * 一段時間（秒，[from, to)）裡有沒有雲端片段（事件詳細的前後片段用：先查片段清單、確定有才在按〔播放〕時建回放 session；
 * 只打開詳細不建 session，不會在稽核留下「看了 … 的雲端錄影」，第九輪 R9-P1-3、B9-06）。
 * 'ok' = 有片段跟這段重疊；'suspect' = 重疊的片段長度都不合理；'none' = 沒有；'unknown' = 讀不到清單。
 */
export async function cloudClipIn(
  api: string,
  homeId: string,
  device: string,
  fromSec: number,
  toSec: number,
  now = Date.now() / 1000,
  lens = 0,
): Promise<'ok' | 'suspect' | 'none' | 'unknown'> {
  try {
    const p = await vms.recordings(api, homeId, device, { from: iso(fromSec - 60), to: iso(toSec + 60), source: 'cloud', limit: 50, ...(lens ? { lens } : {}) });
    const hits = (p?.data ?? [])
      .filter((c) => c.source === 'cloud')
      .map((c) => ({ start: Date.parse(c.start_at) / 1000, end: Date.parse(c.end_at) / 1000 }))
      .filter((r) => r.start < toSec && r.end > fromSec);
    if (!hits.length) return 'none';
    return hits.some((r) => !clipRangeSuspect(r, now)) ? 'ok' : 'suspect';
  } catch {
    return 'unknown';
  }
}

const clipAtCache = new Map<string, Promise<'ok' | 'suspect' | 'none' | 'unknown'>>();
/** cloudClipAt 的快取版（同一台、同一個 10 秒格只問一次；讀不到的不快取）。 */
export function cloudClipAtCached(api: string, homeId: string, device: string, atSec: number, lens = 0): Promise<'ok' | 'suspect' | 'none' | 'unknown'> {
  const key = `${api}|${homeId}/${device}#${lens}@${Math.floor(atSec / 10)}`;
  const hit = clipAtCache.get(key);
  if (hit) return hit;
  const p = cloudClipAt(api, homeId, device, atSec, Date.now() / 1000, lens).then((r) => {
    if (r === 'unknown') clipAtCache.delete(key);
    return r;
  });
  clipAtCache.set(key, p);
  if (clipAtCache.size > 500) clipAtCache.delete(clipAtCache.keys().next().value as string);
  return p;
}
